<template>
  <div class="import-exams">
    <dicom-import-modal
      ref="localImportModal"
      :default-active="isModalActive"
      :disabled="isDisabled"
      badge-color="black"
      icon="mdi-plus"
      icon-color="accent"
      :dark="darkMode"
      :options="importOptions"
      @open-viewer-uploaded="handleOpenViewerUploaded"
      @dicom-import-open="openViewer"
      @dicom-import-upload="upload"
    >
      <template v-slot="{ minimizedSeries, on }">
        <v-list-item :disabled="isDisabled" v-on="on" id="IM1">
          <v-list-item-title @click="$emit('close-import-exams-menu')">
            <v-icon class="mr-2">mdi-folder-multiple-plus</v-icon>
            <span>Local files</span>
            <v-progress-circular
              v-if="minimizedSeries"
              class="ml-2"
              color="accent"
              :indeterminate="isUploading"
              :size="24"
              :value="100"
              :width="3"
            >
              {{ minimizedSeries }}
            </v-progress-circular>
          </v-list-item-title>
        </v-list-item>
      </template>

      <template v-slot:size="{ item }">
        [{{ item.x00280010 }}, {{ item.x00280011 }}]
      </template>
    </dicom-import-modal>

    <v-dialog
      overlay-color="black"
      overlay-opacity="0.7"
      :dark="darkMode"
      :value="Boolean(globalParsingFailure)"
      max-width="300"
    >
      <v-card :dark="darkMode" class="global-parsing-error-card">
        <v-card-title class="headline warning--text">
          Parsing error
        </v-card-title>
        <v-card-text>{{ globalParsingFailure }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :dark="darkMode"
            color="warning"
            text
            @click="globalParsingFailure = null"
          >
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as fs from "@/plugins/file-system-api";
import { mapGetters } from "vuex";
import { setup3rdPartyStudy } from "@/js/api.3rdparty";
import { stacksToSchemaFormat, uploadStudies } from "@/js/utils.dicom";
import { parseFiles } from "@/plugins/dicom/utils";
import {
  dicomImportTableHeaders,
  viewerLayouts,
  schemaToStackMap
} from "@/js/preferences";

const defaultUploadStatus = Object.freeze({
  completed: false,
  errors: {},
  loading: false,
  progress: {},
  byteProgress: {},
  processing: {}
});
const defaultParsingFailureMessage =
  "None of your files can be loaded. Please check that they are valid DICOM files.";

const metadata = [
  ...Object.values(schemaToStackMap.patient),
  ...Object.values(schemaToStackMap.study),
  ...Object.values(schemaToStackMap.series)
];

const flattenUploadErrors = error => {
  if (!error) {
    return [];
  }
  if (error.status) {
    if (error.body) {
      return flattenUploadErrors(error.body);
    }
    if (error.statusText) {
      return [error.statusText];
    }
    return [`Request failed (${error.status})`];
  }
  if (typeof error === "string") {
    return [error];
  }
  if (typeof error === "number" || typeof error === "boolean") {
    return [String(error)];
  }
  if (Array.isArray(error)) {
    return error.flatMap(item => flattenUploadErrors(item));
  }
  if (typeof error === "object") {
    if (error.detail) {
      return flattenUploadErrors(error.detail);
    }
    if (Array.isArray(error.details)) {
      return error.details.flatMap(item => flattenUploadErrors(item));
    }
    return Object.entries(error).flatMap(([key, value]) => {
      if (key === "series_index" || key === "serie_index") {
        return [];
      }
      const nested = flattenUploadErrors(value);
      if (!nested.length) {
        return [];
      }
      if (key === "detail" || key === "details") {
        return nested;
      }
      return nested.map(message => `${key}: ${message}`);
    });
  }
  return [String(error)];
};

const getUploadErrorStrings = error => {
  const messages = flattenUploadErrors(error)
    .map(message =>
      typeof message === "string" ? message.trim() : String(message)
    )
    .filter(Boolean);
  return [...new Set(messages)];
};

export default {
  name: "ImportExams",
  data() {
    return {
      importOptions: {
        allowAnonymization: true,
        headers: dicomImportTableHeaders,
        inputModes: { filesystem: true, pacs: true },
        metadata: [...new Set(metadata)], // uniq metadata
        steps: [] // configured in mounted function
      },
      uploadStatus: { ...defaultUploadStatus },
      hasUploaded: false,
      // Map original UID -> anonymized UID for the most recent upload.
      uploadedSeriesUidMap: {},
      // DB IDs of studies created during the most recent upload.
      uploadedStudyIds: [],
      // isUploading: false
      is3rdPartyUrl: process.env.APP_CUSTOMER.getExternal3rdPartyURL,
      validCustomerReferrer: true,
      dragDepth: 0,
      dropOverlayElement: null,
      globalParsingFailure: null
    };
  },
  mounted() {
    this.importOptions.steps = [
      // step 1
      undefined,
      // step 2
      { actions: [{ disabled: !this.isAuth }] },
      // step 3
      {
        status: { ...defaultUploadStatus }
      }
    ];
    if (this.is3rdPartyUrl) this.checkExternalCustomer();
    this.$root.$on("open-local-import-modal", this.openLocalImportModal);
    window.addEventListener("dragenter", this.onWindowDragEnter);
    window.addEventListener("dragover", this.onWindowDragOver);
    window.addEventListener("dragleave", this.onWindowDragLeave);
    window.addEventListener("drop", this.onWindowDrop);
  },
  beforeDestroy() {
    this.$root.$off("open-local-import-modal", this.openLocalImportModal);
    window.removeEventListener("dragenter", this.onWindowDragEnter);
    window.removeEventListener("dragover", this.onWindowDragOver);
    window.removeEventListener("dragleave", this.onWindowDragLeave);
    window.removeEventListener("drop", this.onWindowDrop);
    this.hideDropOverlay();
  },
  computed: {
    ...mapGetters("auth", ["isAuth"]),
    ...mapGetters("dashboard", ["isUploading"]),
    ...mapGetters(["darkMode"]),
    isUploading() {
      if (
        this.$store.state.dashboard.uploadingData &&
        this.$store.state.dashboard.uploadingData.length &&
        this.$store.state.dashboard.uploadingData.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    isShareLink() {
      return !!this.$route.params.shareId;
    },
    isDisabled() {
      return (
        this.$isDemoUser ||
        this.isShareLink ||
        (this.is3rdPartyUrl && !this.validCustomerReferrer)
      );
    },
    isModalActive() {
      return (
        this.is3rdPartyUrl &&
        this.validCustomerReferrer &&
        !this.$route.params.externalStudyId
      );
    },
    // Disable the global file drag&drop upload while in the viewer: the viewer
    // has its own drag flows (e.g. dragging series across panes) and must not
    // trigger the import modal. The manual import button stays available.
    isDropDisabled() {
      return this.isDisabled || this.$route.name === "viewer";
    }
  },
  methods: {
    hasDroppedFiles(event) {
      return Array.from(event?.dataTransfer?.types || []).includes("Files");
    },
    createDropEventSnapshot(event) {
      const items = Array.from(event?.dataTransfer?.items || []);
      const entries = items
        .map(item =>
          typeof item.webkitGetAsEntry === "function"
            ? item.webkitGetAsEntry()
            : null
        )
        .filter(Boolean);

      if (entries.length) {
        return { entries };
      }

      return { files: Array.from(event?.dataTransfer?.files || []) };
    },
    openLocalImportModal() {
      if (this.isDisabled) {
        return;
      }
      this.hideDropOverlay();
      this.$refs.localImportModal?.open();
    },
    isLocalImportModalOpen() {
      return Boolean(this.$refs.localImportModal?.isOpen);
    },
    ensureDropOverlay() {
      if (this.dropOverlayElement || typeof document === "undefined") {
        return this.dropOverlayElement;
      }

      const overlay = document.createElement("div");
      overlay.className = "import-drop-overlay";
      overlay.innerHTML = `
        <div class="import-drop-overlay__panel">
          <i aria-hidden="true" class="v-icon notranslate mdi mdi-folder-multiple-plus theme--light import-drop-overlay__icon"></i>
          <div class="import-drop-overlay__title">Drop files here</div>
          <div class="import-drop-overlay__text">Release to import DICOM files or folders</div>
        </div>
      `;
      document.body.appendChild(overlay);
      this.dropOverlayElement = overlay;
      return overlay;
    },
    setDropOverlayContent({ icon, title, text, loading = false }) {
      const overlay = this.ensureDropOverlay();
      if (!overlay) {
        return;
      }

      const iconElement = overlay.querySelector(".import-drop-overlay__icon");
      const titleElement = overlay.querySelector(".import-drop-overlay__title");
      const textElement = overlay.querySelector(".import-drop-overlay__text");

      if (iconElement) {
        iconElement.className = [
          "v-icon",
          "notranslate",
          "mdi",
          icon,
          "theme--light",
          "import-drop-overlay__icon"
        ].join(" ");
      }
      if (titleElement) {
        titleElement.textContent = title;
      }
      if (textElement) {
        textElement.textContent = text;
      }
      overlay.classList.toggle("import-drop-overlay--loading", loading);
    },
    showDropOverlay() {
      const overlay = this.ensureDropOverlay();
      if (!overlay) {
        return;
      }
      this.setDropOverlayContent({
        icon: "mdi-folder-multiple-plus",
        title: "Drop files here",
        text: "Release to import DICOM files or folders"
      });
      overlay.classList.toggle("import-drop-overlay--dark", this.darkMode);
      overlay.classList.add("import-drop-overlay--visible");
    },
    showParsingOverlay() {
      const overlay = this.ensureDropOverlay();
      if (!overlay) {
        return;
      }
      this.setDropOverlayContent({
        icon: "mdi-loading",
        title: "Parsing DICOM files",
        text: "Please wait while your files are processed",
        loading: true
      });
      overlay.classList.toggle("import-drop-overlay--dark", this.darkMode);
      overlay.classList.add("import-drop-overlay--visible");
    },
    hideDropOverlay() {
      if (!this.dropOverlayElement) {
        return;
      }
      this.dropOverlayElement.remove();
      this.dropOverlayElement = null;
    },
    async parseGlobalDrop(snapshot) {
      let parsingErrors = [];
      this.globalParsingFailure = null;
      this.showParsingOverlay();

      try {
        const { files, errors } = await fs.readFiles(snapshot);
        parsingErrors.push(...errors);

        const series = await parseFiles(files, this.importOptions.metadata);
        if (!series || !series.length) {
          this.globalParsingFailure = defaultParsingFailureMessage;
          return;
        }

        this.$refs.localImportModal?.openFromParsed({
          errors: parsingErrors,
          series
        });
        this.hideDropOverlay();
      } catch (error) {
        console.error(error);
        this.globalParsingFailure =
          typeof error === "string" ? error : defaultParsingFailureMessage;
      } finally {
        if (this.globalParsingFailure) {
          this.hideDropOverlay();
        }
      }
    },
    onWindowDragEnter(event) {
      if (this.isDropDisabled || !this.hasDroppedFiles(event)) {
        return;
      }
      event.preventDefault();
      if (this.isLocalImportModalOpen()) {
        this.dragDepth = 0;
        this.hideDropOverlay();
        return;
      }
      this.dragDepth += 1;
      this.showDropOverlay();
    },
    onWindowDragOver(event) {
      if (this.isDropDisabled || !this.hasDroppedFiles(event)) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      if (this.isLocalImportModalOpen()) {
        this.dragDepth = 0;
        this.hideDropOverlay();
        return;
      }
      this.showDropOverlay();
    },
    onWindowDragLeave(event) {
      if (this.isDropDisabled || !this.hasDroppedFiles(event)) {
        return;
      }
      if (this.isLocalImportModalOpen()) {
        this.dragDepth = 0;
        this.hideDropOverlay();
        return;
      }
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) {
        this.hideDropOverlay();
      }
    },
    onWindowDrop(event) {
      if (this.isDropDisabled || !this.hasDroppedFiles(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.dragDepth = 0;
      if (this.isLocalImportModalOpen()) {
        this.hideDropOverlay();
        return;
      }
      this.parseGlobalDrop(this.createDropEventSnapshot(event));
    },
    openViewer(data) {
      if (this.$isDemoUser) {
        return;
      }

      this.$store.commit("viewer/setServer", this.hasUploaded);
      const schemaData = stacksToSchemaFormat(data);

      schemaData.forEach(study => {
        study.series.forEach(s => {
          // data is uploading/has been uploaded (hybrid mode): set as already downloaded
          s.download_completed = this.hasUploaded;

          // track also uploading status to activate/deactivate 3D tools
          // (needed if upload ends before opening series in viewer)
          const p = this.uploadStatus.progress[s.id];
          s.upload_completed = p && p[0] === p[1];
        });
      });

      if (this.$route.name !== "viewer") {
        this.$store.commit("viewer/initData", schemaData);
        this.$router.push({ name: "viewer" });
      } else {
        // already in viewer
        this.$store.commit("viewer/initData", schemaData);
        this.$store.commit("viewer/updateLayout", viewerLayouts.stack[0]);
        // reset upload flag
        this.hasUploaded = false;
      }
    },
    updateSeriesProgress(id, value) {
      const progress = this.uploadStatus.progress[id];
      this.$set(progress, 0, value);
    },
    updateUploadStatus(key, value) {
      this.uploadStatus[key] = value;
      this.importOptions.steps[2].status[key] = value;
      if (key === "completed" && value === true) {
        // this.$root.$emit("dashboard-refresh");
        //this.importOptions.steps[2].actions[0].disabled = false;
      }
    },
    // Convert selected series to their anonymized UIDs (if any) before
    // asking the dashboard to open the viewer.
    handleOpenViewerUploaded(uploadedSeries) {
      const mappedSeries = uploadedSeries.map(series => {
        const mappedUid = this.uploadedSeriesUidMap[series.uniqueUID];
        if (!mappedUid) {
          return series;
        }
        return { ...series, uniqueUID: mappedUid };
      });
      this.$root.$emit("open-viewer-uploaded", {
        series: mappedSeries,
        studyIds: [...this.uploadedStudyIds]
      });
    },
    upload(data) {
      if (this.$isDemoUser) {
        return;
      }

      // Init progress
      this.updateUploadStatus("completed", false);
      this.updateUploadStatus("errors", {});
      this.updateUploadStatus("loading", true);
      this.updateUploadStatus("progress", {});
      this.updateUploadStatus("processing", {});
      // Reset mapping so previous uploads don't leak into new selections.
      this.uploadedSeriesUidMap = {};
      this.uploadedStudyIds = [];

      this.$store.commit("viewer/setServer", true);
      this.hasUploaded = true;
      // this.isUploading = true;
      this.$store.commit("dashboard/upateUploadingData", data);
      const updateProgress = obj => {
        if (
          typeof obj.bytesSent === "number" &&
          typeof obj.bytesTotal === "number" &&
          obj.seriesId
        ) {
          if (!this.uploadStatus.byteProgress) {
            this.$set(this.uploadStatus, "byteProgress", {});
          }
          this.$set(this.uploadStatus.byteProgress, obj.seriesId, [
            obj.bytesSent,
            obj.bytesTotal
          ]);
          return;
        }
        if (obj.processing && obj.seriesId) {
          if (!this.uploadStatus.processing) {
            this.$set(this.uploadStatus, "processing", {});
          }
          this.$set(this.uploadStatus.processing, obj.seriesId, obj.processing);
          // Sync series-level completion from processing payload (batch-safe).
          const seriesTotal =
            Number(obj.processing.series_total_files) ||
            Number(obj.processing.total_files) ||
            0;
          const seriesProcessed =
            Number(obj.processing.series_processed_files) ||
            Number(obj.processing.processed_files) ||
            0;
          const seriesComplete =
            obj.processing.series_complete === true ||
            (seriesTotal > 0 && seriesProcessed >= seriesTotal);
          if (seriesComplete && seriesTotal > 0) {
            if (!this.uploadStatus.progress[obj.seriesId]) {
              this.$set(this.uploadStatus.progress, obj.seriesId, [
                seriesTotal,
                seriesTotal
              ]);
            } else {
              this.$set(
                this.uploadStatus.progress[obj.seriesId],
                0,
                seriesTotal
              );
              this.$set(
                this.uploadStatus.progress[obj.seriesId],
                1,
                seriesTotal
              );
            }
          }
          return;
        }
        if (
          typeof obj.batchIndex === "number" &&
          typeof obj.batchTotal === "number" &&
          obj.seriesId
        ) {
          if (!this.uploadStatus.batchInfo) {
            this.$set(this.uploadStatus, "batchInfo", {});
          }
          this.$set(this.uploadStatus.batchInfo, obj.seriesId, [
            obj.batchIndex,
            obj.batchTotal
          ]);
          return;
        }
        if (obj.error) {
          const errorStrings = getUploadErrorStrings(obj.error);

          const errors = this.uploadStatus.errors;
          if (obj.seriesId) {
            errors[obj.seriesId] = errors[obj.seriesId] || [];
            errors[obj.seriesId] = [...errors[obj.seriesId], ...errorStrings];
          } else if (obj.error && typeof obj.error.series_index === "number") {
            errors[obj.error.series_index] =
              errors[obj.error.series_index] || [];
            errors[obj.error.series_index] = [
              ...errors[obj.error.series_index],
              ...errorStrings
            ];
          }

          this.updateUploadStatus("errors", errors);
          // this.isUploading = false;
          if (obj.seriesId) {
            this.$store.commit("dashboard/terminateUploading", obj.seriesId);
          }
        } else if (obj.start) {
          this.$set(this.uploadStatus.progress, obj.seriesId, [
            0,
            obj.numberOfInstances
          ]);
        } else if (obj.end) {
          // !!! need to update VIEWER series upload info
          // (if upload ends when series is already opened in viewer)
          this.$store.commit("viewer/uploadEnded", obj.seriesId);
          // this.uploading = false;
          this.$store.commit("dashboard/terminateUploading", obj.seriesId);
        } else {
          this.updateSeriesProgress(
            obj.seriesId,
            this.uploadStatus.progress[obj.seriesId][0] + 1
          );
        }
      };

      const sub = uploadStudies(data, null)
        .subscribe({
          next: response => {
            // Capture anonymized UID mapping emitted by uploadStudies.
            if (response.uidMap) {
              this.uploadedSeriesUidMap = {
                ...this.uploadedSeriesUidMap,
                ...response.uidMap
              };
              return;
            }
            if (response.startUpload) {
              // this.$root.$emit("dashboard-refresh");
            } else if (response.uploadedStudy) {
              if (response.savedStudyId) {
                this.uploadedStudyIds.push(response.savedStudyId);
              }
              this.$root.$emit("dashboard-refresh");
            } else {
              updateProgress(response);
            }
          },
          error: error => updateProgress(error)
        })
        .add(() => {
          console.warn("UPLOAD COMPLETES");
          this.updateUploadStatus("completed", true);
          this.updateUploadStatus("loading", false);
          this.$store.commit("dashboard/resetUploading");
          this.refreshCurrentUserAfterUpload();
          // this.$root.$emit("dashboard-refresh");
          // this.isUploading = false;
          sub?.unsubscribe();
        });
    },
    checkExternalCustomer() {
      // The viewer must be opened from the 3rd party study provider that
      // serves the DICOM objects: its origin is the one configured in the
      // customer profile (getExternal3rdPartyURL), so a new provider only
      // needs a profile, not a code change.
      const allowedOrigin = new URL(this.is3rdPartyUrl).origin;
      this.validCustomerReferrer =
        window.location.hostname == "localhost" ||
        window.document.referrer.startsWith(allowedOrigin);

      if (!this.validCustomerReferrer) {
        this.$store.commit("raiseSnackbar", {
          text: `Please open the viewer from ${
            new URL(allowedOrigin).hostname
          }`,
          color: "error",
          timeout: -1
        });
        return;
      }

      let studyId = this.$route.params.externalStudyId;
      if (studyId) setup3rdPartyStudy(studyId, this);
    },
    refreshCurrentUserAfterUpload() {
      const auth = this.$http && this.$http.auth;
      if (!auth) return;
      auth
        .getUser()
        .then(user => {
          this.$store.commit("auth/update", { key: "user", value: user });
          this.$store.dispatch("billing/refreshBillingData");
        })
        .catch(() => null);
    }
  }
};
</script>

<style>
td.cell-size {
  min-width: 90px;
}

.import-drop-overlay {
  position: fixed;
  z-index: 9998;
  inset: 0;
  box-sizing: border-box;
  pointer-events: none;
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(250, 250, 250, 0.9);
  font-family: "Montserrat", sans-serif;
}

.import-drop-overlay::before {
  content: "";
  position: absolute;
  inset: 18px;
  border: 3px dashed rgba(var(--v-primary-rgb), 0.82);
  border-radius: 14px;
  box-shadow: inset 0 0 0 1px rgba(var(--v-primary-rgb), 0.18);
}

.import-drop-overlay--visible {
  display: flex;
}

.import-drop-overlay--dark {
  background: rgba(0, 0, 0, 0.82);
  box-shadow: inset 0 0 0 1px rgba(var(--v-primary-rgb), 0.22);
}

.import-drop-overlay__panel {
  max-width: min(420px, calc(100vw - 64px));
  padding: 0 32px;
  text-align: center;
  color: #212121;
}

.import-drop-overlay--dark .import-drop-overlay__panel {
  color: #ffffff;
}

.import-drop-overlay__icon {
  color: var(--v-primary-base) !important;
  font-size: 72px !important;
  margin-bottom: 14px;
}

.import-drop-overlay--loading .import-drop-overlay__icon {
  animation: import-drop-overlay-spin 1s linear infinite;
}

@keyframes import-drop-overlay-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.import-drop-overlay__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
}

.import-drop-overlay__text {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  opacity: 0.72;
  text-transform: uppercase;
}

.global-parsing-error-card {
  border-radius: 8px !important;
}
</style>
